import { useEffect, useSyncExternalStore } from 'react'
import type { Ik, Overzicht } from '../../gedeeld/types'
import { api } from './api'

/* ===========================================================================
 *  Wat de hele app deelt
 *
 *  Dezelfde kleine winkel als in Projects. Het overzicht staat erin, omdat
 *  bijna elk scherm het nodig heeft (de bovenbalk telt de storingen, de
 *  functiepagina leest dezelfde metingen) en het elke halve minuut ververst.
 * =========================================================================== */

export type OverzichtMet = Overzicht & { beschikbaar: Record<string, number | null> }

interface Staat {
  ik: Ik | null
  terugval: boolean
  overzicht: OverzichtMet | null
  overzichtFout: string | null
  overzichtAt: number | null
  meldingen: { id: number; soort: 'ok' | 'fout' | 'info'; tekst: string }[]
}

let staat: Staat = { ik: null, terugval: false, overzicht: null, overzichtFout: null, overzichtAt: null, meldingen: [] }
const luisteraars = new Set<() => void>()

export function zet(deel: Partial<Staat>) {
  staat = { ...staat, ...deel }
  for (const l of luisteraars) l()
}

export function lees(): Staat { return staat }

export function useStaat<T>(kies: (s: Staat) => T): T {
  return useSyncExternalStore((l) => { luisteraars.add(l); return () => { luisteraars.delete(l) } }, () => kies(staat))
}

let volgnummer = 0
export function meld(tekst: string, soort: 'ok' | 'fout' | 'info' = 'ok') {
  const id = ++volgnummer
  zet({ meldingen: [...staat.meldingen, { id, soort, tekst }].slice(-4) })
  setTimeout(() => zet({ meldingen: staat.meldingen.filter((m) => m.id !== id) }), soort === 'fout' ? 8000 : 3200)
}
export const meldFout = (e: unknown) => meld(e instanceof Error ? e.message : String(e), 'fout')

/* ------------------------------------------------------------------ *
 *  Het overzicht, elke halve minuut
 *
 *  Een mislukte vraag gooit het oude overzicht niet weg: dan blijft de
 *  laatste stand staan, met erbij dat hij niet ververst kon worden. Op een
 *  storingspagina is "laatst bekend, 2 minuten oud" veel meer waard dan een
 *  leeg scherm.
 * ------------------------------------------------------------------ */

let bezig: Promise<void> | null = null

export function laadOverzicht(): Promise<void> {
  if (bezig) return bezig
  bezig = api.overzicht()
    .then((o) => zet({ overzicht: o, overzichtFout: null, overzichtAt: Date.now() }))
    .catch((e) => zet({ overzichtFout: e instanceof Error ? e.message : String(e) }))
    .finally(() => { bezig = null })
  return bezig
}

let gebruikers = 0
let klok: ReturnType<typeof setInterval> | null = null

export function useOverzicht() {
  const o = useStaat((s) => s.overzicht)
  const fout = useStaat((s) => s.overzichtFout)
  const at = useStaat((s) => s.overzichtAt)
  useEffect(() => {
    gebruikers++
    if (!lees().overzicht) void laadOverzicht()
    if (!klok) {
      klok = setInterval(() => { if (document.visibilityState === 'visible') void laadOverzicht() }, 30_000)
    }
    return () => {
      gebruikers--
      if (!gebruikers && klok) { clearInterval(klok); klok = null }
    }
  }, [])
  return { overzicht: o, fout, at }
}

/** Een klok die elke vijf seconden tikt, voor "x seconden geleden". */
let tik = Date.now()
const tikLuisteraars = new Set<() => void>()
setInterval(() => { tik = Date.now(); for (const l of tikLuisteraars) l() }, 5000)
export function useNu(): number {
  return useSyncExternalStore((l) => { tikLuisteraars.add(l); return () => { tikLuisteraars.delete(l) } }, () => tik)
}
